// src/ui/screens/CharacterSelect.tsx — UI-02 player-facing character-select screen.
//
// Mount window (boot sequence, D-24): gate → splash → content.ready →
// CharacterSelect → RoundSetup/game. Lifts chosenCharacterId to App.tsx via
// the onSelected callback so the game subtree only mounts once the player
// has picked.
//
// Grid state (default):
//   — Title "Who are you playing as?" (hidden when the Continue banner shows)
//   — Continue banner iff localStorage['hunter-last-character-id'] matches a
//     currently-unlocked character (D-29, T-05-28 mitigation)
//   — 3-col grid of unlocked characters (D-28: Benny filtered server-side
//     by `unlocked===false`)
// Detail state (after tap):
//   — Modal CharacterDetailPane at z-index 160
//   — Back via chevron, Escape key, or browser back (popstate). Escape +
//     popstate listeners only register while a card is focused (Pitfall 11).
//
// Confirm handler writes BOTH:
//   — localStorage['hunter-last-character-id'] (D-29 Continue shortcut)
//   — sessionStorage['hunter:dev.character'] (production propagation to
//     GameScene — it reads this key unconditionally at scene init; the
//     "dev." prefix is historical from P3, the consumer is a production
//     boot path). Sending the write BEFORE onSelected() guarantees Phaser
//     sees the seeded key when the scene boots after CharacterSelect
//     unmounts.
//
// Source: .planning/phases/05-content-pipeline-characters-admin-cms/05-CONTEXT.md D-26..D-30;
//         05-UI-SPEC.md §Layout Contracts + §Copywriting Contract.

import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';

import { getContentBundle, hasContentBundle } from '../../sim/content/cache';
import { CharacterCard } from './char-select/CharacterCard';
import { CharacterDetailPane } from './char-select/CharacterDetailPane';
import './char-select/character-select.css';

const LAST_CHAR_KEY = 'hunter-last-character-id';
const SESSION_CHAR_KEY = 'hunter:dev.character';

export interface CharacterSelectProps {
  onSelected: (id: string) => void;
}

function readLastCharacterId(): string | null {
  try {
    return localStorage.getItem(LAST_CHAR_KEY);
  } catch {
    return null;
  }
}

function writeLastCharacterId(id: string): void {
  try {
    localStorage.setItem(LAST_CHAR_KEY, id);
  } catch {
    /* sandboxed/disabled storage — ignore */
  }
}

function writeSessionCharacterId(id: string): void {
  try {
    sessionStorage.setItem(SESSION_CHAR_KEY, id);
  } catch {
    /* sandboxed iframe — ignore */
  }
}

export function CharacterSelect({ onSelected }: CharacterSelectProps): JSX.Element | null {
  // Mount guard (Test S1): render nothing if content cache is not hydrated.
  // Plan 06's App.tsx gate already holds the game subtree behind content.ready,
  // but belt-and-braces keeps CharacterSelect self-safe under unit tests that
  // mount it without hydrating.
  const ready = hasContentBundle();

  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Escape to close detail pane (registered only while focused).
  useEffect(() => {
    if (focusedId === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setFocusedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusedId]);

  // popstate: browser-back returns to grid (D-26). Push a history entry when
  // opening detail so a subsequent back tap has something to pop; the handler
  // clears focusedId on the synthetic or real popstate.
  useEffect(() => {
    if (focusedId === null) return;
    try {
      window.history.pushState({ detail: focusedId }, '');
    } catch {
      /* test env without history — ignore */
    }
    const onPop = (): void => setFocusedId(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [focusedId]);

  const handleSelect = useCallback((id: string) => setFocusedId(id), []);
  const handleBack = useCallback(() => setFocusedId(null), []);
  const handleConfirm = useCallback(
    (id: string) => {
      writeLastCharacterId(id);
      // Production propagation to GameScene (src/render/scenes/GameScene.ts
      // lines ~333-349) — writes the key BEFORE calling onSelected so that the
      // React tree unmount → Phaser scene boot pipeline sees the seeded value.
      writeSessionCharacterId(id);
      onSelected(id);
    },
    [onSelected],
  );
  const handleDismissBanner = useCallback(() => setBannerDismissed(true), []);

  if (!ready) return null;

  const bundle = getContentBundle();
  // D-28: hidden characters are filtered at the content boundary. Benny is
  // absent from the grid entirely when unlocked=false; the filter is content-
  // side rather than CSS so tampering with the DOM cannot reveal the card.
  const unlockedChars = bundle.characters.filter((c) => c.unlocked);

  const lastId = readLastCharacterId();
  const lastChar = lastId ? unlockedChars.find((c) => c.id === lastId) ?? null : null;
  const showContinueBanner = lastChar !== null && !bannerDismissed && focusedId === null;

  const focusedCharacter = focusedId
    ? unlockedChars.find((c) => c.id === focusedId) ?? null
    : null;

  const handleContinue = (): void => {
    if (lastChar) handleConfirm(lastChar.id);
  };

  return (
    <div className="character-select" data-testid="character-select">
      {showContinueBanner && lastChar !== null && (
        <div className="character-select__continue" data-testid="continue-banner">
          <button
            type="button"
            className="character-select__continue-cta"
            data-testid="continue-cta"
            onClick={handleContinue}
          >
            Continue as {lastChar.inGameName}
          </button>
          <button
            type="button"
            className="character-select__continue-dismiss"
            data-testid="pick-someone-else"
            onClick={handleDismissBanner}
          >
            Pick someone else
          </button>
        </div>
      )}

      {!showContinueBanner && focusedCharacter === null && (
        <h1 className="character-select__title">Who are you playing as?</h1>
      )}

      {focusedCharacter === null && (
        <div className="character-select__grid">
          {unlockedChars.map((c) => (
            <CharacterCard key={c.id} character={c} onSelect={handleSelect} />
          ))}
        </div>
      )}

      {focusedCharacter !== null && (
        <CharacterDetailPane
          character={focusedCharacter}
          onBack={handleBack}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
